import { DateTime } from 'luxon';
import { ToArray } from '../../../../modules/ToArray.js';
import { getCompanyTeamsOnce } from '../../../../modules/katapult-company/CompanyTeams.js';
import { Parallel } from '../../../../modules/Parallel.js';

class AdminExports {
  static PAYROLL_COLUMN_HEADERS = [
    'NAME',
    'EMAIL',
    'SALARIED',
    'SUBMITTED',
    'TOTAL HOURS',
    'REGULAR HOURS (40)',
    'REGULAR HOURS (36)',
    'REGULAR HOURS (32)',
    'OVERTIME (40)',
    'OVERTIME (36)',
    'OVERTIME (32)',
    'Vacation',
    'Sick',
    'Parental Leave',
    'Holiday',
    'BONUS',
    'PT SECONDARY',
    'Ministry',
    'SMART PHONE VOUCHER',
    'Bereavement',
    'Jury Duty',
    'Family Care Leave',
    'Performance-Based PTO',
    'Weekly PTO'
  ];

  static EXCLUDED_ALLOCATIONS = [
    'Vacation',
    'Sick',
    'Parental Leave',
    'Holiday',
    'Ministry',
    'Bereavement',
    'Jury Duty',
    'Family Care Leave',
    'Performance-Based PTO', // (2024-05-28) This is a legacy excluded allocation that needs to remain for historical time sheet reporting
    'Weekly PTO'
  ];

  static LEGACY_ALLOCATIONS = ['Performance-Based PTO'];

  static WEEKLY_PTO_HEADERS = ['Performance-Based PTO', 'Weekly PTO'];

  // Generate payroll export for date range (not using monday as starting date)
  static async payroll(userGroup, startingDay, endingDay, startingMonday, endingMonday) {
    const warnings = [];
    let weekStartDateArray = [];
    // Get the timesheet objects
    let monday = startingMonday;
    while (monday <= endingMonday) {
      weekStartDateArray.push(monday.toISODate());
      monday = monday.plus({ days: 7 });
    }

    let finalDataRows = [];

    // Get all users user data (in array format)
    let allUserData = await FirebaseWorker.ref(`users/${userGroup}`)
      .once('value')
      .then((s) => ToArray(s.val()));

    for (const weekStartDate of weekStartDateArray) {
      // Get timesheet data from firestore
      let timesheetsData = await firebase
        .firestore()
        .collection(`companies/${userGroup}/time_tracking`)
        .where('week_starting', '==', weekStartDate)
        .get();
      let timesheets = timesheetsData.docs.map((doc) => ({ ...doc.data(), $ref: doc.ref }));

      // Summing function
      let sumHours = (docsToSum) => {
        // If there are no docs to sum, return 0
        if (docsToSum.length == 0) return 0;
        else {
          // Otherwise, sum the hours of the docs that are within the date range
          return docsToSum
            .filter(({ date }) => date >= startingDay.toISODate() && date <= endingDay.toISODate())
            .reduce((acc, x) => acc + x.hours, 0);
        }
      };

      // Generate the data rows
      let dataRows = [];
      for (const userData of allUserData) {
        // Get the user's timesheet
        const userTimesheets = timesheets.filter((x) => x.uid == userData.$key);
        let timesheet = userTimesheets[0];

        // Add a warning if the user has more than one timesheet for the week
        if (userTimesheets.length > 1) {
          warnings.push(
            `${userData.email} has more than one timesheet for week ${weekStartDate}. ` +
              `We used the one with more hours; please review their record.`
          );
          timesheet = userTimesheets.reduce((acc, x) => (acc.hours.length > x.hours.length ? acc : x));
        }

        // Add a warning if the user has both the legacy Friday tag (am/pm) and the new Friday tag (default)
        if (
          timesheet?.hours?.some((x) => x.weekday == 'Friday' && (x.tag == 'am' || x.tag == 'pm')) &&
          timesheet?.hours?.some((x) => x.weekday == 'Friday' && x.tag == 'default')
        )
          warnings.push(
            `${userData.email} has both legacy Friday tags and the new Friday tag for week ${weekStartDate}. This may cause an incorrect sum of hours for this pay period. Please review their record.`
          );

        let hours = timesheet?.hours || [];

        // Skip this user if they are not an active user
        if (['inactive', 'locked'].includes(userData.employment_status) && hours.length == 0) continue;

        // Generate the data row for this user
        let dataRow = [];
        let weeklyPTOSum = 0;
        let weeklyPTOColumnCounter = 1;
        for (const header of this.PAYROLL_COLUMN_HEADERS) {
          // Push the user's name
          if (header == 'NAME') dataRow.push(userData?.name ? `"${userData.name.last}, ${userData.name.first}"` : '');
          // Push the user's email
          else if (header == 'EMAIL') dataRow.push(userData?.email || 'unknown');
          // Check to see if the timesheet data was actually submitted
          else if (header == 'SALARIED') dataRow.push(userData?.employment?.salaried || false);
          // Check to see if the timesheet data was actually submitted
          else if (header == 'SUBMITTED') {
            let cellValue;
            // If the timesheet was submitted
            if (timesheet?.submitted) {
              // Try to get the submission timestamp; otherwise, notify admin that it was submitted but no timestamp
              cellValue = !!timesheet.submitted_timestamp
                ? DateTime.fromMillis(timesheet.submitted_timestamp?.seconds * 1000).toLocaleString(DateTime.DATETIME_SHORT_WITH_SECONDS)
                : 'SUBMITTED (NO TIMESTAMP)';
            }
            // Otherwise, the timesheet was not submitted
            else cellValue = 'NOT SUBMITTED';
            dataRow.push(`"${cellValue}"`);
          }
          // Push the sum of all of the user's time data
          else if (header == 'TOTAL HOURS') dataRow.push(sumHours(hours));
          // Push total hours less benefits overhead allocations (and PT Secondary) for 40-hour work week
          else if (header == 'REGULAR HOURS (40)') {
            let totalHoursWorked = sumHours(
              hours.filter((x) => !this.EXCLUDED_ALLOCATIONS.includes(x.time_class) && !x.part_time_secondary)
            );
            dataRow.push(totalHoursWorked > 40 && !userData?.employment?.salaried ? 40 : totalHoursWorked);
          }
          // Push total hours less benefits overhead allocations (and PT Secondary) for 36-hour work week
          else if (header == 'REGULAR HOURS (36)') {
            let totalHoursWorked = sumHours(
              hours.filter((x) => !this.EXCLUDED_ALLOCATIONS.includes(x.time_class) && !x.part_time_secondary)
            );
            dataRow.push(totalHoursWorked > 36 && !userData?.employment?.salaried ? 36 : totalHoursWorked);
          }
          // Push total hours less benefits overhead allocations (and PT Secondary) for 32-hour work week
          else if (header == 'REGULAR HOURS (32)') {
            let totalHoursWorked = sumHours(
              hours.filter((x) => !this.EXCLUDED_ALLOCATIONS.includes(x.time_class) && !x.part_time_secondary)
            );
            dataRow.push(totalHoursWorked > 32 && !userData?.employment?.salaried ? 32 : totalHoursWorked);
          }
          // Push number hours over 40 if total hours worked is greater than 40 (and the user is not salaried) for the 40-hour work week
          else if (header == 'OVERTIME (40)') {
            let totalHoursWorked = sumHours(
              hours.filter((x) => !this.EXCLUDED_ALLOCATIONS.includes(x.time_class) && !x.part_time_secondary)
            );
            dataRow.push(totalHoursWorked > 40 && !userData?.employment?.salaried ? totalHoursWorked - 40 : 0);
          }
          // Push number hours over 36 if total hours worked is greater than 36 (and the user is not salaried) for the 36-hour work week
          else if (header == 'OVERTIME (36)') {
            let totalHoursWorked = sumHours(
              hours.filter((x) => !this.EXCLUDED_ALLOCATIONS.includes(x.time_class) && !x.part_time_secondary)
            );
            dataRow.push(totalHoursWorked > 36 && !userData?.employment?.salaried ? totalHoursWorked - 36 : 0);
          }
          // Push number hours over 32 if total hours worked is greater than 32 (and the user is not salaried) for the 32-hour work week
          else if (header == 'OVERTIME (32)') {
            let totalHoursWorked = sumHours(
              hours.filter((x) => !this.EXCLUDED_ALLOCATIONS.includes(x.time_class) && !x.part_time_secondary)
            );
            dataRow.push(totalHoursWorked > 32 && !userData?.employment?.salaried ? totalHoursWorked - 32 : 0);
          }
          // Push the sum of all PT secondary hours
          else if (header == 'PT SECONDARY') dataRow.push(sumHours(hours.filter((x) => !!x.part_time_secondary)));
          // Smart phone voucher
          else if (header == 'SMART PHONE VOUCHER') dataRow.push(userData?.employment?.employment_type == 'regular' ? 21 : 0);
          // Push bonus amount column
          else if (header == 'BONUS') {
            // Convert the bonus type from a string to a number so we correctly sum the values for reports over multiple weeks
            let bonus = +timesheet?.bonus || 0;
            dataRow.push(bonus);
          } else if (this.WEEKLY_PTO_HEADERS.includes(header)) {
            // Add both Performance-based PTO and Weekly PTO hours into the same column. Once both columns have been added, push the total to the dataRow
            weeklyPTOSum += sumHours(hours.filter((x) => x.time_class == header));
            if (weeklyPTOColumnCounter == this.WEEKLY_PTO_HEADERS.length) dataRow.push(weeklyPTOSum);
            weeklyPTOColumnCounter++;
          }
          // For simple rows, just push the data (or 0 when there is none)
          else dataRow.push(sumHours(hours.filter((x) => x.time_class == header)));
        }

        // Add the data row to the document
        dataRows.push(dataRow);
      }

      dataRows.forEach((x) => {
        // Match by email
        let rowToAddIndex = finalDataRows.findIndex((y) => y[1] == x[1]);
        // If row to add does not exist, add it directly
        if (rowToAddIndex < 0) finalDataRows.push(x);
        // Otherwise, update (sum numbers, "and" bools, leave strings unchanged)
        // TODO (2023-10-20): This is a dangerous way to do this since Javascript doesn't have strict types. If future timesheet or bonus categories are added, the data needs to be formatted as numbers instead of strings so that the report sums correctly over the date range.
        else
          finalDataRows[rowToAddIndex] = finalDataRows[rowToAddIndex].map((y, i) =>
            typeof y == 'number' ? y + x[i] : typeof y == 'boolean' ? y && x[i] : y
          );
      });
    }

    // Sort by last name first
    finalDataRows.sort((a, b) => {
      let name1 = a[0].toUpperCase();
      let name2 = b[0].toUpperCase();
      if (name1 == '' || name2 == '') return name1 == name2 ? 0 : name1 == '' ? 1 : -1;
      return name1 < name2 ? -1 : name1 > name2 ? 1 : 0;
    });

    // Add the column headers to the beginning of the document
    finalDataRows.unshift(
      [`Pay Data Export: ${startingDay.toFormat(`MM/dd/yyyy`)} to ${endingDay.toFormat(`MM/dd/yyyy`)}`],
      this.PAYROLL_COLUMN_HEADERS.filter((x) => !this.LEGACY_ALLOCATIONS.includes(x))
    );
    // Finalize output
    const output = finalDataRows.map((row) => row.join(',')).join('\n');

    // Return the CSV string
    return [output, warnings];
  }

  /**
   * Generates a weekly Key Performance Indicator (KPI) report.
   * @param {string} userGroup - The user group for which the report is generated.
   * @param {string} userId - The user ID of the user generating the report.
   * @param {Object} settings - The settings for the report.
   * @param {DateTime} settings.startDate - The start date for the report.
   * @param {DateTime} settings.endDate - The end date for the report.
   * @param {string} [settings.selectedTeam] - The selected team for the report.
   * @param {boolean} [settings.exportAllTeamsLedByUser] - Whether to export all teams for user leads.
   * @param {boolean} [settings.exportAllTeams] - Whether to export all teams.
   * @param {number} [settings.workWeekHours] - The number of hours in a work week.
   * @param {boolean} [settings.includeNotes] - Whether to include the allocation notes in the report.
   * @returns {Promise<string, Array<string>>} A promise that resolves to an array where the first element is a CSV string representing the report and the second element is an array of warnings.
   * @throws {Error} If there is an error retrieving data from Firestore or generating the report.
   */
  static async teamData(userGroup, userId, settings) {
    const { startDate, endDate, selectedTeam, exportAllTeamsLedByUser, exportAllTeams, workWeekHours = 40, includeNotes } = settings;

    const startingMonday = startDate.set({ weekday: 1 });
    const endingMonday = endDate.set({ weekday: 1 });

    const weekStartDateArray = [];
    const finalDataRows = [];
    const dataRows = [];
    let columnHeaders = [];

    let monday = startingMonday;
    while (monday <= endingMonday) {
      weekStartDateArray.push(/** @type {string} */ (monday.toISODate()));
      monday = monday.plus({ days: 7 });
    }

    const userIdsToExport = await getUserIdsToExport(userGroup, userId, { selectedTeam, exportAllTeamsLedByUser, exportAllTeams });
    const timesheetsByWeekEntries = await getTimesheets(userGroup, weekStartDateArray, userIdsToExport);

    const warnings = [];
    for (const weekStartDate of weekStartDateArray) {
      // Get the timesheets for this week and filter out any that are null
      const unfilteredTimesheetsByUser =
        timesheetsByWeekEntries.find(([weekStartDateEntry]) => weekStartDateEntry == weekStartDate)[1] ?? [];
      const timesheetsByUser = unfilteredTimesheetsByUser.filter(([, timesheet]) => timesheet != null);

      for (let [uid, user] of unfilteredTimesheetsByUser) {
        if (
          timesheetsByUser.some((element) => {
            return element[0] == uid;
          })
        )
          continue;
        const userInfo = (await globalThis.FirebaseWorker.ref(`users/${userGroup}/${uid}`).once('value')).val();
        const name = userInfo?.name?.first && userInfo?.name?.last ? `"${userInfo.name.first} ${userInfo.name.last}"` : userInfo?.email;
        const startTime = userInfo?.employment?.fulltime_start_date;
        const warningText =
          startTime > Date.parse(startDate)
            ? `User ${name} started after the designated start date and is expected to have missing data.`
            : `User ${name} has no submitted hours for the week starting in ${weekStartDate}.`;
        if (!warnings.includes(warningText)) warnings.push(warningText);
      }

      const daysToExport = [];
      let dayToExport = startDate;
      while (dayToExport <= endDate) {
        daysToExport.push({ date: dayToExport.toISODate() });
        dayToExport = dayToExport.plus({ days: 1 });
      }

      // Get a listing of every allocation used across all time documents
      const allTimeClasses = timesheetsByUser.map(([, timesheet]) => timesheet.hours).flat();
      const timeClasses = allTimeClasses.filter((allocation) => daysToExport.some((y) => y.date == allocation.date));
      for (const timeClass of timeClasses) if (!columnHeaders.includes(timeClass.time_class)) columnHeaders.push(timeClass.time_class);
      columnHeaders
        .sort()
        .unshift(
          'NAME',
          'USER',
          'TOTAL HOURS',
          `WORKED HOURS (BASE ${workWeekHours})`,
          'PTO HOURS',
          'OT HOURS',
          `REGULAR HOURS (${workWeekHours})`
        );

      // Get a lookup of uids to time class sums
      const userTimeData = {};
      for (const [, timesheet] of timesheetsByUser) {
        userTimeData[timesheet.uid] = userTimeData[timesheet.uid] || {};
        // Add the time allocation data to this user's bucket
        timesheet.hours.reverse();
        for (const entry of timesheet.hours) {
          // Skip if this day is not one to export
          if (!daysToExport.some((x) => x.date == entry.date)) continue;
          // Otherwise, proceed
          let existingHours = userTimeData[timesheet.uid][entry.time_class] ?? 0;
          userTimeData[timesheet.uid][entry.time_class] = existingHours + entry.hours;
        }
      }

      for (const uid in userTimeData) {
        const userInfo = (await globalThis.FirebaseWorker.ref(`users/${userGroup}/${uid}`).once('value')).val();
        const userIsSalaried = userInfo?.employment?.salaried || false;
        const email = userInfo?.email || 'unknown';
        const name = userInfo?.name?.last ? `"${userInfo.name.last}, ${userInfo.name.first}"` : 'unknown';
        const hours = Object.entries(userTimeData[uid]);

        // Generate the data row for this user
        const dataRow = {};

        for (const header of columnHeaders) {
          if (!dataRow[header]) dataRow[header] = 0;
          // Push the user's email
          if (header == 'USER') dataRow[header] = email;
          // Push the user's name
          else if (header == 'NAME') dataRow[header] = name;
          // Push the sum of all of the user's time data
          else if (header == 'TOTAL HOURS') {
            const totalHours = hours.reduce((acc, x) => (acc += x[1]), 0);
            dataRow[header] = totalHours;
          } else if (header == `WORKED HOURS (BASE ${workWeekHours})`) {
            const totalHours = hours.filter((x) => !this.EXCLUDED_ALLOCATIONS.includes(x[0])).reduce((acc, x) => (acc += x[1]), 0);
            dataRow[header] = totalHours;
          } else if (header == 'PTO HOURS') {
            const totalHours = hours.filter((x) => this.EXCLUDED_ALLOCATIONS.includes(x[0])).reduce((acc, x) => (acc += x[1]), 0);
            dataRow[header] = totalHours;
          } else if (header == 'OT HOURS') {
            const otHours = userIsSalaried ? 0 : dataRow[`WORKED HOURS (BASE ${workWeekHours})`] - workWeekHours;
            const totalHours = otHours > 0 ? otHours : 0;
            dataRow[header] = totalHours;
          } else if (header == `REGULAR HOURS (${workWeekHours})`) {
            const totalHours = userIsSalaried
              ? dataRow[`WORKED HOURS (BASE ${workWeekHours})`]
              : dataRow['TOTAL HOURS'] - dataRow['OT HOURS'] - dataRow['PTO HOURS'];
            dataRow[header] = totalHours;
          }
          // For simple rows, just push the data (or 0 when there is none)
          else dataRow[header] = userTimeData[uid][header] || 0;
        }

        // Add the data row to the document
        dataRows.push(dataRow);
      }
    }
    // Delete repeated headers
    columnHeaders = columnHeaders.filter((x, i) => columnHeaders.indexOf(x) == i);
    let usersArray = [];
    // Convert the final objects to rows
    for (const obj of dataRows) {
      // If the email exists in finalDataRows, add the hours to the existing row
      let existingRow = usersArray.find((x) => x.USER == obj.USER);
      if (existingRow) {
        let row = existingRow;
        for (const header of columnHeaders) {
          if (header != 'USER' && header != 'NAME') {
            if (!obj[header] || obj[header].isNaN || obj[header] == undefined) obj[header] = 0;
            if (
              !row[columnHeaders.indexOf(header)] ||
              row[columnHeaders.indexOf(header)].isNaN ||
              row[columnHeaders.indexOf(header)] == undefined
            )
              existingRow[header] += obj[header] || 0;
          }
        }
      } else {
        let user = {};
        for (const header of columnHeaders) {
          user[header] = obj[header] ?? 0;
        }
        usersArray.push(user);
      }
    }
    for (const user of usersArray) {
      finalDataRows.push(Object.values(user));
    }

    // Sort by last name first
    finalDataRows.sort((a, b) => {
      let name1 = a[0].toUpperCase();
      let name2 = b[0].toUpperCase();
      if (name1 == '' || name2 == '') return name1 == name2 ? 0 : name1 == '' ? 1 : -1;
      return name1 < name2 ? -1 : name1 > name2 ? 1 : 0;
    });

    finalDataRows.unshift(columnHeaders);
    // Finalize output
    return [finalDataRows.map((row) => row.join(',')).join('\n'), warnings];
  }

  static async endOfWeekSurvey(userGroup, monday) {
    let output = [];

    // Get the timesheet objects
    let timesheetsData = await firebase
      .firestore()
      .collection(`companies/${userGroup}/time_tracking`)
      .where('week_starting', '==', monday.toISODate())
      .get();
    let timesheets = timesheetsData.docs.map((doc) => ({ ...doc.data(), $ref: doc.ref }));

    // Get all users data (in array format)
    let allUserData = await FirebaseWorker.ref(`users/${userGroup}`)
      .once('value')
      .then((s) => ToArray(s.val()));
    allUserData = allUserData.sort((a, b) => {
      let aString = a.name ? `${a.name.last}, ${a.name.first}` : a.email;
      let bString = b.name ? `${b.name.last}, ${b.name.first}` : b.email;
      return aString.localeCompare(bString);
    });

    // For each user data object, get their timesheet and create csv rows for their data
    for (const userData of allUserData) {
      // Get the timesheet
      let timesheet = timesheets.find((x) => x.uid == userData.$key);
      let hours = timesheet?.hours || [];

      // Skip this user if they are not an active user
      if (['inactive', 'locked'].includes(userData.employment_status) && hours.length == 0) continue;

      // Push the user's name (or email)
      output.push(userData.name ? `"${userData.name.last}, ${userData.name.first}"` : userData.email);
      output.push(`,Vision Note:,"${timesheet?.vision_note?.replace(/"/g, '""')}"`);
      output.push(`,Growth:,"${timesheet?.growth_rating} / 10"`);
      output.push(`,General Note:,"${timesheet?.general_note?.replace(/"/g, '""')}"`);
      output.push('');
    }

    return [output.join(`\n`), null];
  }

  static async unpaidLeave(userGroup, startingMonday, endingMonday) {
    let output = [
      `Start Date:,${startingMonday.toISODate()}`,
      `End Date:,${endingMonday.plus({ days: 6 }).toISODate()}`,
      '',
      'Employee Email, Total Unpaid Leave (hrs)'
    ];

    // Get all users data
    let users = await FirebaseWorker.ref(`users/${userGroup}`)
      .once('value')
      .then((s) => s.val());

    // Get overhead categories
    let overheadTimeAllocations = await FirebaseWorker.ref(`photoheight/company_space/${userGroup}/models/overhead_time_allocations`)
      .once('value')
      .then((s) => (s.val() ?? []).map((x) => x.name));

    // Get the timesheet objects
    while (startingMonday <= endingMonday) {
      let timesheetsData = await firebase
        .firestore()
        .collection(`companies/${userGroup}/time_tracking`)
        .where('week_starting', '==', startingMonday.toISODate())
        .get();
      timesheetsData.docs.forEach((doc) => {
        let timesheet = doc.data();
        if (users[timesheet.uid]) {
          users[timesheet.uid].timesheets ??= [];
          users[timesheet.uid].timesheets.push(timesheet);
        }
      });
      startingMonday = startingMonday.plus({ days: 7 });
    }

    let userTotals = [];

    // For each user data object, get their timesheet and create csv rows for their data
    for (const uid in users) {
      let userData = users[uid];
      let unpaidLeave = 0;
      let fullTimeStartDate = DateTime.fromMillis(userData.employment?.fulltime_start_date ?? Infinity);
      if (
        userData.employment?.employment_type == 'regular' &&
        !['inactive', 'locked'].includes(userData.employment_status) &&
        userData.timesheets
      ) {
        userData.timesheets.forEach((timesheet) => {
          let totalWorkedHours = 0;
          let totalClockedHours = 0;
          timesheet.hours?.forEach((time) => {
            totalClockedHours += time.hours;
            if (!overheadTimeAllocations.includes(time.time_class)) {
              totalWorkedHours += time.hours;
            }
          });
          let hoursToWork = 0;
          let saturday = DateTime.fromISO(timesheet.week_starting).plus({ days: 5 });

          // If the user starts Saturday or later, don't expect them to have worked any hours that week
          if (fullTimeStartDate >= saturday) hoursToWork = 0;
          // If the user started before Monday, expect them to have worked 40 hours
          else if (fullTimeStartDate < DateTime.fromISO(timesheet.week_starting)) hoursToWork = 40;
          // If the user started between on or after Monday and before Saturday, expect them to have worked 8 hours per day that they were active
          else hoursToWork = saturday.diff(fullTimeStartDate, 'days').toFormat('d') * 8;

          // Only track unpaid leave for weeks where they were full-time employees (after their full-time start date)
          if (hoursToWork > 0) {
            if (totalClockedHours < hoursToWork) unpaidLeave += hoursToWork - totalClockedHours;
          }
        });
        userTotals.push({ email: userData.email, unpaidLeave });
      }
    }

    userTotals.sort((a, b) => a.email.localeCompare(b.email));

    output.push(...userTotals.map((x) => `${x.email},${x.unpaidLeave}`));

    return [output.join(`\n`), null];
  }

  /**
   * Checks the teams that a user has access to and fetches relevant data.
   * This function does the following:
   * - Fetches the user's data from Firebase.
   * - Fetches all teams from Firebase.
   * - If the user is an admin, fetches all users from Firebase.
   * - If the user is not an admin, fetches all users from a specific path in Firebase.
   * - Creates an array of team names and a list of team UIDs that the user can view.
   * - Updates several properties of the `this` object with data fetched from Firebase.
   * @param {string} uid - The unique identifier of the user.
   * @throws {Error} If there is an error retrieving data from Firebase.
   * @returns {Promise<string[]>} A promise that resolves to an array of user UIDs.
   *
   */
  static async checkTeams(uid) {
    let allMembers;
    const userAccessPath = `users/katapult/${uid}`;
    const user = await FirebaseWorker.ref(userAccessPath)
      .once('value')
      .then((s) => s.val());

    const teamAccessPath = `photoheight/company_space/katapult/team_health/teams`;
    let allTeams = await FirebaseWorker.ref(teamAccessPath)
      .once('value')
      .then((s) => s.val());
    if (allTeams === null) {
      allTeams = {};
    }

    // If the user is an admin, fetch user data from /users
    if (user?.team_health?.admin) {
      allMembers = await FirebaseWorker.ref('users/katapult')
        .once('value')
        .then((s) => s.val());
    } else {
      allMembers = await FirebaseWorker.ref('photoheight/company_space/katapult/users')
        .once('value')
        .then((s) => s.val());
    }

    // Makes an array of team names to fill the main dropdown menu
    // and a list of team uids for teams that the user can view
    let availableTeams = [];
    if (user?.team_health?.available_teams && Object.keys(allTeams).length !== 0) {
      availableTeams = user?.team_health?.available_teams;
    }
    //filter allMembers to only include users that are in the available teams
    allMembers = Object.keys(allMembers)
      .filter((key) => availableTeams.includes(allMembers[key].team_id) || key == uid)
      .reduce((obj, key) => {
        obj[key] = allMembers[key];
        return obj;
      }, {});
    return Object.keys(allMembers);
  }
}

async function getUserIdsToExport(userGroup, userId, { selectedTeam, exportAllTeamsLedByUser, exportAllTeams }) {
  const allTeams = await getCompanyTeamsOnce(firebase.firestore(), userGroup);

  if (exportAllTeams) {
    const allTeamMembersWithPotentialDuplicates = allTeams.flatMap(([, team]) => team.members);
    const allUniqueTeamMembers = [...new Set(allTeamMembersWithPotentialDuplicates)];
    return allUniqueTeamMembers;
  }

  if (exportAllTeamsLedByUser) {
    const teamsLedByUser = allTeams.filter(([, team]) => team.leaders.includes(userId));
    const teamMembersLeadByUserWithPotentialDuplicates = teamsLedByUser.flatMap(([, team]) => team.members);
    const uniqueTeamMembersLeadByUser = [...new Set(teamMembersLeadByUserWithPotentialDuplicates)];
    return uniqueTeamMembersLeadByUser;
  }

  return allTeams.find(([teamId]) => teamId == selectedTeam)?.[1]?.members || [];
}

/**
 * Fetches timesheets for certain users across multiple weeks.
 * @param {string} userGroup - The user group for which the timesheets are fetched.
 * @param {string[]} weeksToFetch - An array of week start dates (in ISO format).
 * @param {string[]} userIds - An array of user IDs for which to fetch timesheets.
 * @returns {Promise<Array>}
 */
async function getTimesheets(userGroup, weeksToFetch, userIds) {
  const timesheetsCollection = firebase.firestore().collection(`companies/${userGroup}/time_tracking`);

  const timesheetsByWeek = await Parallel.map(
    weeksToFetch,
    async (weekStartDate) => {
      const timesheets = await Parallel.map(
        userIds,
        async (userId) => {
          const queryResults = await timesheetsCollection.where('week_starting', '==', weekStartDate).where('uid', '==', userId).get();
          const timesheet = queryResults.docs[0]?.data();
          return [userId, timesheet];
        },
        10
      );
      return [weekStartDate, timesheets];
    },
    10
  );

  return timesheetsByWeek;
}

export { AdminExports };
